import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, Routes, Route, useParams } from "react-router-dom";
import {
  Briefcase, FileText, Upload, LogOut, ExternalLink, Loader2, CheckCircle2, XCircle,
  Clock, ShieldCheck, Trash2, AlertTriangle, Download,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../../lib/AuthContext";
import { Logo } from "../../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (v) => {
  const n = Number(v || 0);
  return `R ${n.toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const DECISION_META = {
  pending: { icon: Clock, color: "text-amber-600 bg-amber-50 border-amber-200", label: "Awaiting your decision" },
  accepted: { icon: CheckCircle2, color: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "Accepted" },
  rejected: { icon: XCircle, color: "text-red-700 bg-red-50 border-red-200", label: "Rejected" },
  expired: { icon: AlertTriangle, color: "text-slate-700 bg-slate-100 border-slate-300", label: "Expired" },
};

/* ================ Layout ================ */
export const ClientLayout = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const onLogout = async () => { await logout(); nav("/login", { replace: true }); };

  return (
    <div className="min-h-screen bg-[#f5f6f6] flex flex-col" data-testid="client-layout">
      <header className="sticky top-0 z-30 bg-[#20282c] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link to="/client" className="flex items-center gap-3">
            <Logo variant="group" size="sm" dark />
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs font-display uppercase tracking-widest text-[#ffc438]" data-testid="client-user-email">
              {user?.email}
            </span>
            <Link to="/" className="h-9 w-9 rounded-md hover:bg-white/10 flex items-center justify-center" title="Website" data-testid="client-back-site">
              <ExternalLink size={16} />
            </Link>
            <button type="button" onClick={onLogout}
                    className="h-9 w-9 rounded-md hover:bg-white/10 flex items-center justify-center" title="Sign out"
                    data-testid="client-logout">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 py-5 pb-24">
        <Outlet />
      </main>
      <nav className="fixed bottom-0 inset-x-0 z-30 bg-white border-t border-[#cfd7db] shadow-[0_-4px_16px_rgba(0,0,0,0.05)]">
        <div className="max-w-4xl mx-auto grid grid-cols-2">
          <TabLink to="/client" end icon={Briefcase} label="My jobs" testid="client-nav-jobs" />
          <TabLink to="/client/documents" icon={FileText} label="Documents" testid="client-nav-documents" />
        </div>
      </nav>
    </div>
  );
};

const TabLink = ({ to, end, icon: Icon, label, testid }) => (
  <NavLink to={to} end={end} data-testid={testid}
           className={({ isActive }) => `py-3 flex flex-col items-center gap-1 font-display uppercase tracking-widest text-xs transition ${isActive ? "text-[#f28a16]" : "text-[#3f4b52]"}`}>
    <Icon size={20} />
    {label}
  </NavLink>
);

/* ================ Dashboard / Job list ================ */
export const ClientJobs = () => {
  const [jobs, setJobs] = useState(null);
  const [customer, setCustomer] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [me, j] = await Promise.all([
          axios.get(`${API}/client/me`),
          axios.get(`${API}/client/jobs`),
        ]);
        setCustomer(me.data.customer);
        setJobs(j.data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not load your jobs");
      }
    })();
  }, []);

  if (!jobs) return <div className="p-8 text-center"><Loader2 className="animate-spin text-[#f28a16] inline" /></div>;

  return (
    <div className="space-y-5" data-testid="client-dashboard">
      <div>
        <div className="inline-flex items-center gap-2 text-xs font-display uppercase tracking-widest text-[#f28a16] bg-[#f28a16]/10 px-2 py-1 rounded-sm">
          <ShieldCheck size={12} /> Client Portal
        </div>
        <h1 className="mt-2 font-display font-black uppercase text-3xl sm:text-4xl text-[#111619]">
          {customer?.display_name || "Your dashboard"}
        </h1>
        {customer?.code && (
          <p className="text-xs text-[#3f4b52] mt-1">Customer code · <span data-testid="client-customer-code">{customer.code}</span></p>
        )}
      </div>

      {jobs.length === 0 ? (
        <div className="bg-white border border-dashed border-[#cfd7db] rounded-md p-8 text-center" data-testid="client-empty-jobs">
          <Briefcase className="mx-auto text-[#3f4b52] mb-3" />
          <p className="text-sm text-[#3f4b52]">No jobs linked to your account yet.</p>
          <p className="text-xs text-[#3f4b52] mt-1">When Diesel &amp; Dust prepares a quote, it will appear here.</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {jobs.map((j) => {
            const dec = DECISION_META[j.client_decision || "pending"] || DECISION_META.pending;
            const Icon = dec.icon;
            return (
              <li key={j.id}>
                <Link to={`/client/jobs/${j.id}`} data-testid={`client-job-${j.id}`}
                      className="block bg-white rounded-md border border-[#cfd7db] p-4 hover:border-[#f28a16] transition">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xs text-[#3f4b52] font-display uppercase tracking-widest">
                        Job #{j.job_number} · {j.quotation_number || "—"}
                        {j.tax_invoice_number ? ` · ${j.tax_invoice_number}` : ""}
                      </div>
                      <div className="font-display font-black uppercase text-lg text-[#111619] mt-1 truncate">
                        {j.vehicle_make_model || "Vehicle"}
                      </div>
                      <div className="text-xs text-[#3f4b52] mt-1">
                        {j.registration || "—"} · {j.fitment_date ? `Fitment ${j.fitment_date}` : "Not scheduled"}
                      </div>
                    </div>
                    <div className={`shrink-0 inline-flex items-center gap-1.5 border rounded-sm px-2 py-1 text-xs font-display uppercase tracking-widest ${dec.color}`}>
                      <Icon size={12} /> {dec.label}
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

/* ================ Job detail — accept / reject ================ */
export const ClientJobDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [reasons, setReasons] = useState({});
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [j, r] = await Promise.all([
        axios.get(`${API}/client/jobs/${id}`),
        axios.get(`${API}/client/reject-reasons`),
      ]);
      setJob(j.data);
      setReasons(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load job");
      nav("/client", { replace: true });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!job) return <div className="p-8 text-center"><Loader2 className="animate-spin text-[#f28a16] inline" /></div>;

  const decision = job.client_decision || "pending";
  const canDecide = decision === "pending" && job.quotation_number;
  const dec = DECISION_META[decision] || DECISION_META.pending;
  const Icon = dec.icon;

  const accept = async () => {
    if (!window.confirm(`Accept quote ${job.quotation_number}?\n\nThis lets Diesel & Dust know you're going ahead. They will contact you to confirm the fitment date, time and fitter.`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/client/jobs/${id}/accept`);
      toast.success("Quote accepted — the team has been notified.");
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not accept"); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    if (!reason) return toast.error("Please choose a reason");
    setBusy(true);
    try {
      await axios.post(`${API}/client/jobs/${id}/reject`, { reason, comment });
      toast.success("Quote declined — the team has been notified.");
      setRejectOpen(false);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not reject"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="client-job-detail">
      <Link to="/client" className="text-xs text-[#3f4b52] hover:text-[#f28a16] font-display uppercase tracking-widest" data-testid="client-back">← Back to jobs</Link>

      <div className={`inline-flex items-center gap-2 border rounded-sm px-3 py-1.5 text-xs font-display uppercase tracking-widest ${dec.color}`} data-testid="client-decision-badge">
        <Icon size={14} /> {dec.label}
      </div>

      <h1 className="font-display font-black uppercase text-3xl text-[#111619]">
        {job.vehicle_make_model || "Job"} · #{job.job_number}
      </h1>

      <div className="grid sm:grid-cols-2 gap-3">
        <Info label="Registration" value={job.registration} />
        <Info label="Year" value={job.vehicle_year} />
        <Info label="Quotation" value={job.quotation_number} />
        <Info label="Tax invoice" value={job.tax_invoice_number} />
        <Info label="Fitment date" value={job.fitment_date || "To be confirmed"} />
        <Info label="Fitment address" value={job.fitment_address} />
      </div>

      <div className="bg-white rounded-md border border-[#cfd7db] p-4">
        <div className="text-xs font-display uppercase tracking-widest text-[#3f4b52]">Quote total</div>
        <div className="font-display font-black text-3xl mt-1 text-[#111619]" data-testid="client-quote-total">
          {money((job.retail || 0) + (job.fitment_fee || 0) + (job.moulding || 0))}
        </div>
        <div className="text-[11px] text-[#3f4b52] mt-1">
          Excludes VAT unless stated on the printed quotation. Ask us for a detailed breakdown.
        </div>
      </div>

      {decision === "rejected" && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-xs" data-testid="client-reject-info">
          <div className="font-display uppercase tracking-widest text-red-700">Declined</div>
          <div className="text-[#3f4b52] mt-1">
            Reason: <strong>{reasons[job.client_decision_reason] || job.client_decision_reason || "—"}</strong>
          </div>
          {job.client_decision_comment && (
            <div className="text-[#3f4b52] mt-1">Note: {job.client_decision_comment}</div>
          )}
        </div>
      )}

      {job.tax_invoice_number && (
        <a href={`${API}/admin/jobs/${id}/invoice.pdf`} target="_blank" rel="noreferrer"
           data-testid="client-invoice-pdf"
           className="inline-flex items-center gap-2 border border-[#cfd7db] rounded-md px-4 py-2.5 text-sm font-display uppercase tracking-widest text-[#111619] hover:border-[#f28a16]">
          <Download size={14} /> Download tax invoice
        </a>
      )}

      {canDecide && (
        <div className="bg-white rounded-md border border-[#cfd7db] p-4 space-y-3">
          <div className="text-sm font-display uppercase tracking-widest text-[#111619]">Your decision</div>
          <p className="text-xs text-[#3f4b52]">
            Accepting notifies us that you&apos;d like to go ahead. We&apos;ll then contact you to confirm date, time and fitter.
            Accepting does <strong>not</strong> book a fitment slot on its own.
          </p>
          <div className="flex gap-3">
            <button onClick={accept} disabled={busy}
                    data-testid="client-accept-btn"
                    className="flex-1 bg-emerald-600 text-white rounded-md py-3 font-display uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />}
              Accept quote
            </button>
            <button onClick={() => setRejectOpen(true)} disabled={busy}
                    data-testid="client-reject-btn"
                    className="flex-1 bg-white border border-red-300 text-red-700 rounded-md py-3 font-display uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60">
              <XCircle size={16} /> Decline
            </button>
          </div>
        </div>
      )}

      {rejectOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-3" onClick={() => setRejectOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-md p-4 w-full max-w-md space-y-3" data-testid="reject-modal">
            <div className="text-sm font-display uppercase tracking-widest">Decline this quote</div>
            <select value={reason} onChange={(e) => setReason(e.target.value)}
                    data-testid="reject-reason"
                    className="w-full h-11 border border-[#cfd7db] rounded-md px-3 text-sm">
              <option value="">Choose a reason…</option>
              {Object.entries(reasons).map(([k, v]) => (<option key={k} value={k}>{v}</option>))}
            </select>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3}
                      data-testid="reject-comment"
                      placeholder="Optional comment (max 1000 chars)"
                      className="w-full border border-[#cfd7db] rounded-md px-3 py-2 text-sm" />
            <div className="flex gap-2">
              <button onClick={() => setRejectOpen(false)} className="flex-1 border rounded-md py-2 text-sm">Cancel</button>
              <button onClick={reject} disabled={busy || !reason} data-testid="reject-confirm"
                      className="flex-1 bg-red-600 text-white rounded-md py-2 text-sm disabled:opacity-60">
                Confirm decline
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Info = ({ label, value }) => (
  <div className="bg-white rounded-md border border-[#cfd7db] p-3">
    <div className="text-xs font-display uppercase tracking-widest text-[#3f4b52]">{label}</div>
    <div className="text-sm text-[#111619] mt-1">{value || "—"}</div>
  </div>
);

/* ================ Documents ================ */
export const ClientDocuments = () => {
  const [docs, setDocs] = useState(null);
  const [purpose, setPurpose] = useState("id_document");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setDocs((await axios.get(`${API}/documents`)).data); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not load documents"); }
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a file first");
    if (file.size > 10 * 1024 * 1024) return toast.error("Max 10 MB per file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("purpose", purpose);
      fd.append("file", file);
      await axios.post(`${API}/documents/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      setFile(null);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await axios.delete(`${API}/documents/${id}`);
      toast.success("Deleted");
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not delete"); }
  };

  if (!docs) return <div className="p-8 text-center"><Loader2 className="animate-spin text-[#f28a16] inline" /></div>;

  return (
    <div className="space-y-5" data-testid="client-documents">
      <div>
        <h1 className="font-display font-black uppercase text-3xl text-[#111619]">Documents</h1>
        <p className="text-xs text-[#3f4b52] mt-1">Upload ID, insurance letters, proof of address or damage photos. Files stay private to your account.</p>
      </div>

      <form onSubmit={upload} className="bg-white rounded-md border border-[#cfd7db] p-4 space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-display uppercase tracking-widest text-[#3f4b52] block mb-1">Purpose</span>
            <select value={purpose} onChange={(e) => setPurpose(e.target.value)}
                    data-testid="doc-purpose"
                    className="w-full h-11 border border-[#cfd7db] rounded-md px-3 text-sm">
              <option value="id_document">ID document</option>
              <option value="insurance_letter">Insurance letter</option>
              <option value="proof_of_address">Proof of address</option>
              <option value="damage_photo">Damage photo</option>
              <option value="other">Other</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-display uppercase tracking-widest text-[#3f4b52] block mb-1">File (image or PDF · max 10 MB)</span>
            <input type="file" accept="image/*,application/pdf"
                   data-testid="doc-file"
                   onChange={(e) => setFile(e.target.files?.[0])}
                   className="w-full text-sm" />
          </label>
        </div>
        <button type="submit" disabled={busy || !file} data-testid="doc-upload"
                className="w-full sm:w-auto bg-[#f28a16] text-white rounded-md px-6 py-2.5 text-sm font-display uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}
          Upload
        </button>
      </form>

      {docs.length === 0 ? (
        <div className="bg-white border border-dashed border-[#cfd7db] rounded-md p-8 text-center text-sm text-[#3f4b52]" data-testid="doc-empty">
          No documents yet.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="doc-list">
          {docs.map((d) => (
            <li key={d.id} className="bg-white rounded-md border border-[#cfd7db] p-3 flex items-center gap-3">
              {d.content_type?.startsWith("image/") && (
                <img alt={d.original_filename}
                     src={`${API}/documents/${d.id}/download?tok=${d.download_token}`}
                     className="w-12 h-12 rounded-sm object-cover border border-[#cfd7db]" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[#111619] truncate">{d.original_filename}</div>
                <div className="text-[11px] text-[#3f4b52]">
                  {d.purpose.replace(/_/g, " ")} · {(d.size / 1024).toFixed(1)} KB
                </div>
              </div>
              <a href={`${API}/documents/${d.id}/download?tok=${d.download_token}`}
                 target="_blank" rel="noreferrer" data-testid={`doc-view-${d.id}`}
                 className="text-xs text-[#f28a16] hover:underline">View</a>
              <button onClick={() => del(d.id)} data-testid={`doc-delete-${d.id}`}
                      className="text-red-600 hover:bg-red-50 rounded-md h-8 w-8 inline-flex items-center justify-center">
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
